import { Box, makeStyles } from '@material-ui/core';
import { exportActiveChatToCrm } from '@src/actions/agentconsole';
import useContainerWidth from '@src/hooks/useContainerWidth';
import { ServiceConnectors } from '@src/model/frontendmodel';
import { DARK_BG_CARD } from '@src/muithemes/darkModeColors';
import {
  activeChatSelector,
  isActiveChatInBotStatusSelector,
} from '@src/selectors/chats';
import { selectAuthenticationInfo } from '@src/selectors/user';
import { RootState } from '@src/store/reduxstore';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactTagsListView from '../AdminHome/Tags/ContactTagsListView';
import ActionButtons, { ActionButtonsType } from './ActionButtons';
import CloseButton from './CloseButton';
import ContactInfo from './ContactInfo';
import CRMButtonWrapper from './CRMButton';
import MoreOptions from './MoreOptions';
import ResumeButtonWrapper from './ResumeButton';
import TakeChatButtonWrapper from './TakeChatButton';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '80px',
    padding: '16px 24px 16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? DARK_BG_CARD
        : theme.palette.background.paper,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
}));

const AgentToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const { root } = useStyles();

  const [showTags, setShowTags] = useState(false);
  const agentToolbarRef = useRef<HTMLDivElement>(null);

  const agentToolbarWidth = useContainerWidth(agentToolbarRef);

  const isContextMenuOpened = useSelector(
    (state: RootState) => state.agentConsole.chatContextMenu.isOpened
  );

  const activeChat = useSelector(activeChatSelector);

  const authenticationInfo = useSelector(selectAuthenticationInfo);

  const disableAgentToolbarButton = useSelector(
    isActiveChatInBotStatusSelector
  );

  let activeChatServiceConnector: string | null = null;
  let crmProviderType = 'ticket';

  if (
    activeChat?.crmProvider === ServiceConnectors.FRESHDESK ||
    authenticationInfo?.enabledConnectorTypes?.includes(
      ServiceConnectors.FRESHDESK
    )
  ) {
    activeChatServiceConnector = ServiceConnectors.FRESHDESK;
    crmProviderType = 'ticketFreshdesk';
  }
  if (
    activeChat?.crmProvider === ServiceConnectors.ZENDESK ||
    authenticationInfo?.enabledConnectorTypes?.includes(
      ServiceConnectors.ZENDESK
    )
  ) {
    activeChatServiceConnector = ServiceConnectors.ZENDESK;
  }

  const handleExportToCrm = (crmConnector: string) => {
    if (activeChat && !!activeChat?.id) {
      dispatch(exportActiveChatToCrm(activeChat.id, crmConnector));
    }
  };

  if (!activeChat?.id) return <Box width="100%" className={root} />;

  return (
    <Box
      id="agent-toolbar-container"
      data-testid="agent-toolbar"
      width="100%"
      className={root}
    >
      <Box display="flex" justifyContent="space-between">
        <ContactInfo
          fullName={activeChat?.contact?.fullName || ''}
          avatarUrl=""
          contactId={activeChat?.contact?.id}
          agentToolbarWidth={agentToolbarWidth}
        />

        <Box
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ref={agentToolbarRef}
          display="flex"
          alignItems="center"
          style={{ gap: '12px' }}
        >
          <TakeChatButtonWrapper />
          <ResumeButtonWrapper />
          <ActionButtons
            type={'tags' as ActionButtonsType}
            onClick={() => setShowTags(!showTags)}
          />
          <CRMButtonWrapper disabled={disableAgentToolbarButton} />
          <ActionButtons
            type={crmProviderType as ActionButtonsType}
            onClick={() =>
              handleExportToCrm(activeChatServiceConnector as string)
            }
            disabled={
              !!(activeChat?.crmTicketId && activeChat?.crmProvider) ||
              disableAgentToolbarButton
            }
          />
          <CloseButton disabled={disableAgentToolbarButton} />
          <MoreOptions disabled={disableAgentToolbarButton} />
        </Box>
      </Box>
      {showTags && (
        <ContactTagsListView
          isContextMenuOpened={isContextMenuOpened}
          contactId={activeChat?.contact?.id}
          chatId={activeChat.id}
          style={{ gap: '45px' }}
        />
      )}
    </Box>
  );
};

export default AgentToolbar;
